import { supabase } from '../lib/supabase';
import { invalidateUserCache, setUserCache } from './userCacheService';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Linking } from 'react-native';
import Constants from 'expo-constants';
import * as WebBrowser from 'expo-web-browser';

WebBrowser.maybeCompleteAuthSession();

// ==================== RATE LIMITING ====================
const RATE_LIMIT_KEY = '@auth_rate_limit';
const MAX_ATTEMPTS = 3;
const INITIAL_LOCKOUT_MS = 30000; // 30 seconds

const getRateLimitState = async () => {
    try {
        const data = await AsyncStorage.getItem(RATE_LIMIT_KEY);
        return data ? JSON.parse(data) : { attempts: 0, lockoutUntil: 0 };
    } catch {
        return { attempts: 0, lockoutUntil: 0 };
    }
};

const setRateLimitState = async (state) => {
    await AsyncStorage.setItem(RATE_LIMIT_KEY, JSON.stringify(state));
};

const resetRateLimit = async () => {
    await AsyncStorage.removeItem(RATE_LIMIT_KEY);
};

const OAUTH_REDIRECT_TO = 'uniquest://auth-callback';
const OAUTH_TIMEOUT_MS = 60000;

const isExpoGo = () => Constants.appOwnership === 'expo';

const getProviderLabel = (provider) => {
    const labels = {
        google: 'Google',
        github: 'GitHub',
        linkedin_oidc: 'LinkedIn',
    };

    return labels[provider] || provider;
};

const getOAuthAvatarUrl = (metadata = {}) => {
    return metadata.avatar_url || metadata.picture || metadata.photo || null;
};

const getOAuthDisplayName = (user) => {
    const metadata = user?.user_metadata || {};

    return (
        metadata.nome ||
        metadata.full_name ||
        metadata.name ||
        user?.email?.split('@')?.[0] ||
        'Usuário'
    );
};

const getOAuthUsername = (user) => {
    const metadata = user?.user_metadata || {};
    const rawBase = String(metadata.preferred_username || metadata.user_name || user?.email?.split('@')?.[0] || 'user');
    const base = rawBase
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9_]+/g, '_')
        .replace(/^_+|_+$/g, '')
        .slice(0, 24) || 'user';
    const suffix = user?.id?.replace(/-/g, '').slice(0, 8) || Date.now().toString(36);

    return `${base}_${suffix}`;
};

const getOAuthAvatarSeed = (user) => {
    return user?.id?.replace(/-/g, '').slice(0, 12) || 'happy';
};

const parseAuthCallbackUrl = (url) => {
    const params = {};
    const parts = url.split(/[?#]/).slice(1);

    parts.forEach((part) => {
        part.split('&').forEach((pair) => {
            if (!pair) return;
            const [rawKey, rawValue = ''] = pair.split('=');
            const key = decodeURIComponent(rawKey.replace(/\+/g, ' '));
            const value = decodeURIComponent(rawValue.replace(/\+/g, ' '));
            params[key] = value;
        });
    });

    return params;
};

const openOAuthSession = async (authUrl, redirectTo) => {
    let subscription;
    let timeoutId;

    const linkingPromise = new Promise((resolve) => {
        subscription = Linking.addEventListener('url', ({ url }) => {
            if (url?.startsWith(redirectTo)) {
                resolve({ type: 'success', url });
            }
        });
    });

    const timeoutPromise = new Promise((resolve) => {
        timeoutId = setTimeout(() => {
            resolve({
                type: 'timeout',
                error: `Tempo esgotado ao aguardar retorno do login. Verifique se ${OAUTH_REDIRECT_TO} esta liberado no Supabase e teste em uma build nativa.`,
            });
        }, OAUTH_TIMEOUT_MS);
    });

    try {
        const result = await Promise.race([
            WebBrowser.openAuthSessionAsync(authUrl, redirectTo),
            linkingPromise,
            timeoutPromise,
        ]);

        if (result?.type === 'timeout') {
            WebBrowser.dismissBrowser();
        }

        return result;
    } finally {
        if (timeoutId) clearTimeout(timeoutId);
        subscription?.remove?.();
    }
};

/**
 * Sincroniza metadados do OAuth com public.profiles.
 * Nao sobrescreve foto ja escolhida pelo usuario.
 */
export const syncOAuthProfile = async (user) => {
    try {
        if (!user?.id) {
            return { success: false, error: 'Usuário não informado.' };
        }

        const metadata = user.user_metadata || {};
        const avatarUrl = getOAuthAvatarUrl(metadata);
        const displayName = getOAuthDisplayName(user);
        const username = getOAuthUsername(user);
        const avatarSeed = getOAuthAvatarSeed(user);

        const { data: profile, error: selectError } = await supabase
            .from('profiles')
            .select('id, nome, username, foto_url, avatar_seed')
            .eq('id', user.id)
            .maybeSingle();

        if (selectError) throw selectError;

        if (!profile) {
            const { error: insertError } = await supabase
                .from('profiles')
                .insert({
                    id: user.id,
                    email: user.email,
                    nome: displayName,
                    username,
                    foto_url: avatarUrl,
                    avatar_seed: avatarSeed,
                });

            if (insertError) throw insertError;
            return { success: true };
        }

        const updates = {};

        if (!profile.foto_url && avatarUrl) {
            updates.foto_url = avatarUrl;
        }

        if (!profile.nome && displayName) {
            updates.nome = displayName;
        }

        if (!profile.username && username) {
            updates.username = username;
        }

        if (!profile.avatar_seed && avatarSeed) {
            updates.avatar_seed = avatarSeed;
        }

        if (Object.keys(updates).length === 0) {
            return { success: true };
        }

        const { error: updateError } = await supabase
            .from('profiles')
            .update({ ...updates, updated_at: new Date().toISOString() })
            .eq('id', user.id);

        if (updateError) throw updateError;
        return { success: true };
    } catch (error) {
        return { success: false, error: error.message };
    }
};

// ==================== PASSWORD VALIDATION ====================
/**
 * Valida força da senha
 * Requisitos: mínimo 8 caracteres, pelo menos 1 letra e 1 número
 */
const validatePassword = (password) => {
    if (!password || password.length < 8) {
        return { valid: false, error: 'Senha deve ter pelo menos 8 caracteres' };
    }
    if (!/[a-zA-Z]/.test(password)) {
        return { valid: false, error: 'Senha deve conter pelo menos 1 letra' };
    }
    if (!/[0-9]/.test(password)) {
        return { valid: false, error: 'Senha deve conter pelo menos 1 número' };
    }
    return { valid: true };
};

// ==================== MENSAGENS DE ERRO ====================

// POR QUÊ: o `fetch` customizado de `src/lib/supabase.js` converte requisição
// pendurada em `TimeoutError: Supabase request timeout (10000ms)`, e o gotrue-js
// reembrulha isso como `AuthRetryableFetchError` com `status: 0`. Sem tradução
// esse texto ia cru para o Alert da tela de login. O gotrue também responde em
// inglês ("Invalid login credentials"), idem. Aqui é o único ponto onde erro de
// auth vira texto para o usuário.

const NETWORK_ERROR_MESSAGE = 'Sem conexão com o servidor. Verifique sua internet e tente novamente.';
const SERVER_ERROR_MESSAGE = 'Servidor temporariamente indisponível. Tente novamente em instantes.';

const isNetworkError = (error) => {
    if (!error) return false;
    if (error.name === 'TimeoutError' || error.name === 'AbortError') return true;
    // AuthRetryableFetchError cobre falha de rede E 5xx; só é rede quando não há status HTTP.
    if (error.name === 'AuthRetryableFetchError' && !error.status) return true;
    if (error.status === 0) return true;
    return /timeout|network request failed|failed to fetch/i.test(String(error.message || ''));
};

const AUTH_ERROR_MESSAGES = [
    [/invalid login credentials/i, 'E-mail ou senha incorretos.'],
    [/email not confirmed/i, 'Confirme seu e-mail antes de entrar — verifique sua caixa de entrada.'],
    [/already registered|already exists/i, 'Este e-mail já tem uma conta. Faça login ou recupere a senha.'],
    [/email_address_invalid|email address .* is invalid|invalid email/i, 'E-mail inválido. Confira o endereço digitado.'],
    [/password should be at least/i, 'Senha muito curta — use pelo menos 8 caracteres.'],
    [/for security purposes|rate limit|too many requests/i, 'Muitas tentativas em pouco tempo. Aguarde um minuto e tente de novo.'],
    [/user not found/i, 'Não encontramos uma conta com esse e-mail.'],
];

const getAuthErrorMessage = (error, fallback) => {
    if (isNetworkError(error)) return NETWORK_ERROR_MESSAGE;
    if (error?.status >= 500) return SERVER_ERROR_MESSAGE;

    const raw = error?.message || error?.error_description || '';
    const haystack = `${error?.code || ''} ${raw}`;
    const match = AUTH_ERROR_MESSAGES.find(([pattern]) => pattern.test(haystack));
    return match ? match[1] : (raw || fallback);
};

// ==================== AUTENTICAÇÃO ====================

/**
 * Cadastrar novo usuário com email e senha
 * Inclui validação de força de senha
 */
export const signUp = async (email, password, nome) => {
    try {
        // S06: Validar força da senha antes de enviar ao Supabase
        const passwordValidation = validatePassword(password);
        if (!passwordValidation.valid) {
            return { success: false, error: passwordValidation.error };
        }

        const { data, error } = await supabase.auth.signUp({
            email,
            password,
            options: {
                data: {
                    nome: nome,
                },
            },
        });

        if (error) {
            console.log('[signUp] Supabase error:', JSON.stringify({
                message: error.message, status: error.status, code: error.code, name: error.name,
            }));
            throw error;
        }
        return { success: true, data };
    } catch (error) {
        const friendlyError = getAuthErrorMessage(error, 'Erro desconhecido ao criar conta.');
        return { success: false, error: friendlyError };
    }
};

/**
 * Login com email e senha
 * Inclui rate limiting com delay exponencial
 */
export const signIn = async (email, password) => {
    try {
        // S07: Verificar rate limit antes de tentar login
        const rateLimit = await getRateLimitState();
        const now = Date.now();

        if (rateLimit.lockoutUntil > now) {
            const remainingSeconds = Math.ceil((rateLimit.lockoutUntil - now) / 1000);
            return {
                success: false,
                error: `Muitas tentativas. Aguarde ${remainingSeconds} segundos.`,
                rateLimited: true
            };
        }

        const { data, error } = await supabase.auth.signInWithPassword({
            email,
            password,
        });

        if (error) {
            // Falha de REDE não é tentativa de senha errada. Sem esta guarda, 3 timeouts
            // em Wi-Fi ruim disparam o lockout de 30s com "Muitas tentativas" — mensagem
            // errada e punição por um problema que não é do usuário.
            if (isNetworkError(error)) throw error;

            // Incrementar contador de falhas
            const newAttempts = rateLimit.attempts + 1;
            if (newAttempts >= MAX_ATTEMPTS) {
                // Delay exponencial: 30s, 60s, 120s, 240s...
                const lockoutMs = INITIAL_LOCKOUT_MS * Math.pow(2, newAttempts - MAX_ATTEMPTS);
                await setRateLimitState({
                    attempts: newAttempts,
                    lockoutUntil: now + lockoutMs
                });
            } else {
                await setRateLimitState({ ...rateLimit, attempts: newAttempts });
            }
            throw error;
        }

        // Login bem-sucedido: resetar rate limit
        await resetRateLimit();
        return { success: true, data };
    } catch (error) {
        return { success: false, error: getAuthErrorMessage(error, 'Erro desconhecido ao entrar.') };
    }
};

/**
 * Login social via Supabase OAuth.
 * Usa o browser do sistema e volta pelo scheme uniquest://auth-callback.
 */
export const signInWithProvider = async (provider) => {
    try {
        const providerLabel = getProviderLabel(provider);
        console.log(`[OAuth] Iniciando login com ${providerLabel} → redirectTo: ${OAUTH_REDIRECT_TO}`);

        if (isExpoGo()) {
            return {
                success: false,
                error: `Login com ${providerLabel} nao funciona dentro do Expo Go. Use uma build nativa/de desenvolvimento para o app conseguir receber o retorno ${OAUTH_REDIRECT_TO}.`,
            };
        }

        const { data, error } = await supabase.auth.signInWithOAuth({
            provider,
            options: {
                redirectTo: OAUTH_REDIRECT_TO,
                skipBrowserRedirect: true,
            },
        });

        if (error) {
            console.log('[OAuth] signInWithOAuth error:', JSON.stringify({
                message: error.message, status: error.status, code: error.code,
            }));
            throw error;
        }
        if (!data?.url) {
            console.log('[OAuth] data.url ausente — Supabase não gerou URL de auth');
            return { success: false, error: 'URL de autenticação não foi gerada. Provider Google pode não estar ativo no Supabase.' };
        }

        console.log(`[OAuth] Abrindo browser → ${data.url.slice(0, 80)}...`);
        const result = await openOAuthSession(data.url, OAUTH_REDIRECT_TO);
        console.log(`[OAuth] openOAuthSession result.type: ${result?.type}`);

        if (!result) {
            return { success: false, error: 'Login cancelado.' };
        }

        if (result.type === 'timeout') {
            return { success: false, error: result.error };
        }

        if (result.type !== 'success' || !result.url) {
            return { success: false, cancelled: true, error: 'Login cancelado.' };
        }

        const params = parseAuthCallbackUrl(result.url);

        if (params.error || params.error_description) {
            return {
                success: false,
                error: params.error_description || params.error || 'Erro no login social.',
            };
        }

        if (params.code) {
            const { data: sessionData, error: exchangeError } =
                await supabase.auth.exchangeCodeForSession(params.code);

            if (exchangeError) throw exchangeError;
            if (sessionData?.user) {
                await syncOAuthProfile(sessionData.user);
                setUserCache(sessionData.user);
            }
            return { success: true, data: sessionData };
        }

        if (params.access_token && params.refresh_token) {
            const { data: sessionData, error: sessionError } = await supabase.auth.setSession({
                access_token: params.access_token,
                refresh_token: params.refresh_token,
            });

            if (sessionError) throw sessionError;
            if (sessionData?.user) {
                await syncOAuthProfile(sessionData.user);
                setUserCache(sessionData.user);
            }
            return { success: true, data: sessionData };
        }

        return { success: false, error: 'Não foi possível concluir a sessão OAuth — callback sem code/token.' };
    } catch (error) {
        console.log('[OAuth] catch error:', error);
        const friendlyError = getAuthErrorMessage(error, 'Erro desconhecido no login social.');
        return { success: false, error: friendlyError };
    }
};

export const signInWithGoogle = () => signInWithProvider('google');


/**
 * Logout
 */
export const signOut = async () => {
    try {
        // Invalidar cache do usuário antes do logout
        invalidateUserCache();

        const { error } = await supabase.auth.signOut();
        if (error) throw error;
        return { success: true };
    } catch (error) {
        return { success: false, error: error.message };
    }
};

/**
 * Exclusão de conta (exigência da Play Store / LGPD). Fluxo:
 *   1. Reautentica com a senha (confirma que quem está no aparelho é o dono).
 *   2. Invoca a Edge Function `delete-account` — que apaga auth.users + dados
 *      (cascade). A identidade no servidor vem do JWT, não da senha enviada.
 *   3. Em sucesso, encerra a sessão local (o AuthContext leva ao login).
 * Retorna { success, error } — nunca lança.
 */
export const deleteAccount = async (password) => {
    try {
        const { data: { session } } = await supabase.auth.getSession();
        const email = session?.user?.email;
        if (!email) {
            return { success: false, error: 'Sessão expirada. Entre novamente e tente de novo.' };
        }

        // 1. Reautenticação: valida a senha (também renova o JWT usado no passo 2).
        const { error: reauthError } = await supabase.auth.signInWithPassword({ email, password });
        if (reauthError) {
            return { success: false, error: 'Senha incorreta.' };
        }

        // 2. Função apaga a conta + dados (envia o JWT do usuário automaticamente).
        const { data, error } = await supabase.functions.invoke('delete-account');
        if (error || data?.error) {
            return { success: false, error: data?.error || error?.message || 'Não foi possível excluir a conta.' };
        }

        // 3. Conta removida no servidor → encerra a sessão local.
        invalidateUserCache();
        await supabase.auth.signOut();
        return { success: true };
    } catch (error) {
        return { success: false, error: error?.message || 'Erro ao excluir a conta.' };
    }
};

/**
 * Obter usuário atual (usa cache para evitar chamadas duplicadas)
 */
export const getCurrentUser = async () => {
    try {
        const { getCachedUser } = await import('./userCacheService');
        return await getCachedUser();
    } catch (error) {
        return null;
    }
};

/**
 * Obter sessão atual
 */
export const getSession = async () => {
    try {
        const { data: { session }, error } = await supabase.auth.getSession();
        if (error) throw error;
        return session;
    } catch (error) {
        return null;
    }
};

/**
 * Resetar senha (envia email)
 */
export const resetPassword = async (email) => {
    try {
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
            redirectTo: 'uniquest://reset-password',
        });
        if (error) throw error;
        return { success: true };
    } catch (error) {
        return { success: false, error: getAuthErrorMessage(error, 'Erro ao enviar o e-mail de recuperação.') };
    }
};

/**
 * Listener para mudanças de autenticação
 */
export const onAuthStateChange = (callback) => {
    return supabase.auth.onAuthStateChange((event, session) => {
        callback(event, session);
    });
};

// ==================== PERFIL ====================

/**
 * Obter perfil do usuário
 */
/**
 * Discriminated Result do fetch de perfil. Substitui o anti-pattern
 * `return data | null` que conflate três estados semanticamente distintos:
 *   - 'ok':        perfil carregado com sucesso
 *   - 'not_found': consulta válida mas sem registro (PostgREST PGRST116)
 *   - 'error':     falha (rede, RLS, timeout) — o caller deve decidir retry
 *
 * Justificativa: callers tomam decisões diferentes em cada estado.
 * 'not_found' pode disparar criação de perfil padrão; 'error' justifica
 * retry; 'ok' segue o fluxo normal.
 */
export const PROFILE_RESULT_STATUS = Object.freeze({
    OK: 'ok',
    NOT_FOUND: 'not_found',
    ERROR: 'error',
});

// Colunas de profiles legíveis pelo client. email/provider/provider_id/
// raw_user_meta_data ficam fora do grant de SELECT (correção #2 — migration
// 20260610130000): select('*') em profiles falha com 42501. O e-mail do
// próprio usuário vem da sessão de auth (supabase.auth.getUser()).
// ⚠️ `curso`, `areas_interesse`, `ano_conclusao` e `semestre_atual` PRECISAM estar
// aqui: a trava de onboarding (App.js) decide pelo `profile.curso`. Se a coluna
// não vier no select, ela chega `undefined` e a trava dispara para TODOS os
// usuários, para sempre. Coluna nova em profiles → revisar esta lista.
const PROFILE_PUBLIC_COLUMNS = 'id, nome, username, avatar_seed, foto_url, foto_perfil, bio, is_verified, is_premium, xp_total, nivel, streak_atual, streak_maximo, questoes_respondidas, questoes_corretas, daily_goal, daily_answered, daily_correct, followers_count, total_study_time_minutes, last_study_date, created_at, updated_at, curso, areas_interesse, ano_conclusao, semestre_atual';

export const getProfile = async (userId) => {
    try {
        const { data, error } = await supabase
            .from('profiles')
            .select(PROFILE_PUBLIC_COLUMNS)
            .eq('id', userId)
            .single();

        // PostgREST: PGRST116 = "JSON object requested, multiple (or no) rows returned"
        // Distingue "sem registro" (estado legítimo) de "falha de rede".
        if (error) {
            if (error.code === 'PGRST116') {
                return { status: PROFILE_RESULT_STATUS.NOT_FOUND, data: null };
            }
            throw error;
        }
        return { status: PROFILE_RESULT_STATUS.OK, data };
    } catch (error) {
        // Breadcrumb mínimo para debug — não trava o fluxo.
        // Em produção (DEV strip via babel) o console.warn não escapa do bundle.
        console.warn('[authService.getProfile] failed', {
            userId,
            code: error?.code,
            message: error?.message,
        });
        return { status: PROFILE_RESULT_STATUS.ERROR, data: null, error };
    }
};


/**
 * Atualizar perfil do usuário
 */
export const updateProfile = async (userId, updates) => {
    try {
        const { data, error } = await supabase
            .from('profiles')
            .update({ ...updates, updated_at: new Date().toISOString() })
            .eq('id', userId)
            .select(PROFILE_PUBLIC_COLUMNS)
            .single();

        if (error) throw error;
        return { success: true, data };
    } catch (error) {
        return { success: false, error: error.message };
    }
};

/**
 * Upload de foto de perfil
 */
export const uploadProfilePhoto = async (userId, file) => {
    try {
        const fileExt = file.uri.split('.').pop();
        const fileName = `${userId}.${fileExt}`;
        const filePath = `avatars/${fileName}`;

        // Fazer upload
        const { error: uploadError } = await supabase.storage
            .from('avatars')
            .upload(filePath, file, { upsert: true });

        if (uploadError) throw uploadError;

        // Obter URL pública
        const { data: { publicUrl } } = supabase.storage
            .from('avatars')
            .getPublicUrl(filePath);

        // Atualizar perfil com nova foto
        await updateProfile(userId, { foto_url: publicUrl });

        return { success: true, url: publicUrl };
    } catch (error) {
        return { success: false, error: error.message };
    }
};

// ==================== RANKING ====================

/**
 * Obter ranking global por XP
 */
export const getGlobalRanking = async (limit = 50) => {
    try {
        const { data, error } = await supabase
            .from('profiles')
            .select('id, nome, foto_url, nivel, xp_total')
            .order('xp_total', { ascending: false })
            .limit(limit);

        if (error) throw error;
        return data;
    } catch (error) {
        return [];
    }
};

export default {
    signUp,
    signIn,
    signInWithProvider,
    signInWithGoogle,
    syncOAuthProfile,
    signOut,
    getCurrentUser,
    getSession,
    resetPassword,
    onAuthStateChange,
    getProfile,
    updateProfile,
    uploadProfilePhoto,
    getGlobalRanking,
};
